import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Drawer,
  Form,
  Input,
  InputNumber,
  List,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Timeline,
  Typography,
  Upload,
  message,
} from "antd";
import {
  CheckCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  FileTextOutlined,
  PlusOutlined,
  ReloadOutlined,
  SendOutlined,
  StopOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import Settings from "../../dependencies/custom/settings";
import Utilities from "../../dependencies/custom/react-utilities";
import ValuesStore from "../../store/values-store";
import { createLmsApi, getLmsErrorMessage, getLmsListItems } from "../../lms/api";
import {
  LMS_CONTENT_STATUSES,
  LMS_CONTENT_TYPES,
  LMS_MAX_FILE_SIZE,
  LMS_VALID_FILE_EXTENSIONS,
  statusColor,
} from "../../lms/constants";
import {
  LmsErrorAlert,
  LmsOwnershipTag,
  LmsPageHeader,
  canEditLmsRow,
  formatDateTime,
} from "../../lms/components/LmsPrimitives";

const { Text, Title } = Typography;

function formatBytes(bytes) {
  if (!bytes) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function assignmentLabel(assignment) {
  const subject = assignment.subjectName || "General";
  const stream = assignment.streamName ? ` · ${assignment.streamName}` : "";
  return `${subject} · ${assignment.className || "Class"}${stream}`;
}

function contentFormValues(record, sessions = [], subjectTeachers = []) {
  if (!record) return {};
  const session = sessions.find((item) => item.title === record.sessionTitle);
  const assignmentIds = (record.subjectTeacherDetails || [])
    .map((detail) => {
      const match = subjectTeachers.find(
        (assignment) =>
          assignment.teacherName === detail.teacherName &&
          assignment.subjectName === detail.subjectName &&
          assignment.className === detail.className &&
          (assignment.streamName || null) === (detail.streamName || null),
      );
      return match?.id;
    })
    .filter(Boolean);
  return {
    lmsSessionID: record.lmsSessionID || session?.id,
    subjectTeacherIDs: record.subjectTeacherIDs || assignmentIds,
    title: record.title,
    description: record.description || "",
    content_type: record.content_type,
    week_number: record.week_number,
  };
}

const LmsContent = () => {
  const navigate = useNavigate();
  const api = useMemo(() => createLmsApi(navigate), [navigate]);
  const store = ValuesStore();
  const user = store.getValue("user") || {};
  const permissions = store.getValue("permissions") || [];
  const can = (permission) =>
    user.is_superuser === true ||
    user.isSuperuser === true ||
    permissions.some((entry) => entry.name === permission);
  const [form] = Form.useForm();
  const [rejectForm] = Form.useForm();
  const [content, setContent] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [subjectTeachers, setSubjectTeachers] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [filters, setFilters] = useState({ search: "", lms_session: undefined, status: undefined, content_type: undefined });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [approvalHistory, setApprovalHistory] = useState([]);
  const [approvalLoading, setApprovalLoading] = useState(false);
  const [approvalError, setApprovalError] = useState("");
  const approvalRequestID = useRef(0);
  const [rejectRecord, setRejectRecord] = useState(null);

  const loadApprovalHistory = useCallback(async (contentId) => {
    if (!contentId) return;
    const requestID = ++approvalRequestID.current;
    setApprovalHistory([]);
    setApprovalLoading(true);
    setApprovalError("");
    const response = await api.request("GET", "contentApprovals", {
      pathParams: { id: contentId },
    });
    if (requestID !== approvalRequestID.current) return;
    if (response?.ok) {
      setApprovalHistory(Array.isArray(response.data) ? response.data : []);
    } else {
      setApprovalError(getLmsErrorMessage(response, "Unable to load approval history"));
    }
    setApprovalLoading(false);
  }, [api]);

  useEffect(() => {
    if (detailOpen && detail?.id) loadApprovalHistory(detail.id);
  }, [detailOpen, detail?.id, loadApprovalHistory]);

  const loadContent = useCallback(
    async (nextPage = 1, nextPageSize = 20) => {
      setLoading(true);
      setError("");
      const response = await api.content.list({
        page: nextPage,
        pageSize: nextPageSize,
        search: filters.search || undefined,
        lms_session: filters.lms_session,
        status: filters.status,
        content_type: filters.content_type,
      });
      if (response?.ok) {
        const result = getLmsListItems(response.data);
        setContent(result.items);
        setPagination({ current: nextPage, pageSize: nextPageSize, total: result.count });
      } else {
        setError(getLmsErrorMessage(response, "Unable to load LMS content"));
      }
      setLoading(false);
    },
    [api, filters],
  );

  const loadDependencies = useCallback(async () => {
    const [sessionsResponse, assignmentsResponse] = await Promise.all([
      api.sessions.list({ page: 1, pageSize: 100 }),
      Utilities.fetchWithAuth("GET", `${Settings.urls.academics.my_subjects}?current=true`, undefined, navigate),
    ]);
    if (sessionsResponse?.ok) setSessions(getLmsListItems(sessionsResponse.data).items);
    if (assignmentsResponse?.ok) {
      const assignments = Array.isArray(assignmentsResponse.data)
        ? assignmentsResponse.data
        : assignmentsResponse.data?.results || [];
      setSubjectTeachers(assignments);
    }
  }, [api, navigate]);

  useEffect(() => {
    loadDependencies();
  }, [loadDependencies]);

  useEffect(() => {
    loadContent(1, pagination.pageSize);
    // Filter changes intentionally reset the server page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search, filters.lms_session, filters.status, filters.content_type]);

  const refresh = () => {
    loadDependencies();
    loadContent(1, pagination.pageSize);
  };

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue(contentFormValues(null));
    setFileList([]);
    setFormOpen(true);
  };

  const openEdit = async (record) => {
    if (record?.mine === false) {
      message.error("Read-only: you did not create this content and have not inherited it.");
      return;
    }
    const response = await api.content.get(record.id);
    if (!response?.ok) {
      message.error(getLmsErrorMessage(response, "Unable to load content"));
      return;
    }
    setEditing(response.data);
    form.setFieldsValue(contentFormValues(response.data, sessions, subjectTeachers));
    setFileList([]);
    setFormOpen(true);
  };

  const handleSave = async (values) => {
    const body = {
      lmsSessionID: values.lmsSessionID,
      subjectTeacherIDs: values.subjectTeacherIDs,
      title: values.title,
      description: values.description || "",
      content_type: values.content_type,
      week_number: values.week_number || undefined,
    };
    setSaving(true);
    const response = editing
      ? await api.content.update(editing.id, body)
      : await api.content.create(body);
    let uploadResponse = null;
    if (response?.ok && fileList.length) {
      uploadResponse = await api.content.uploadFiles(
        response.data.id,
        fileList.map((item) => item.originFileObj || item),
      );
    }
    setSaving(false);
    if (!response?.ok) {
      message.error(getLmsErrorMessage(response, "Unable to save content"));
      return;
    }
    if (fileList.length && !uploadResponse?.ok) {
      message.warning(`Content saved, but files were not uploaded: ${getLmsErrorMessage(uploadResponse)}`);
    } else {
      message.success(editing ? "Content updated" : "Content created");
    }
    setFormOpen(false);
    setEditing(null);
    setFileList([]);
    loadContent(1, pagination.pageSize);
  };

  const runAction = async (action, record, successMessage) => {
    const response = await action(record.id);
    if (!response?.ok) {
      message.error(getLmsErrorMessage(response, "Unable to update content"));
      return false;
    }
    message.success(successMessage);
    setDetail(response.data || record);
    if (detailOpen && detail?.id === record.id) loadApprovalHistory(record.id);
    loadContent(1, pagination.pageSize);
    return true;
  };

  const handleReject = async (values) => {
    const succeeded = await runAction(
      (id) => api.content.reject(id, values.rejectionReason),
      rejectRecord,
      "Content rejected",
    );
    if (!succeeded) return;
    setRejectRecord(null);
    rejectForm.resetFields();
  };

  const handleDelete = async (record) => {
    const response = await api.content.remove(record.id);
    if (!response?.ok) {
      message.error(getLmsErrorMessage(response, "Unable to delete content"));
      return;
    }
    message.success("Content deleted");
    if (detail?.id === record.id) setDetailOpen(false);
    loadContent(1, pagination.pageSize);
  };

  const handleDeleteFile = async (file) => {
    const response = await api.content.deleteFile(detail.id, file.id);
    if (!response?.ok) {
      message.error(getLmsErrorMessage(response, "Unable to delete file"));
      return;
    }
    setDetail((current) => ({
      ...current,
      contentFiles: (current.contentFiles || []).filter((item) => item.id !== file.id),
    }));
    message.success("File removed");
  };

  const beforeUpload = (file) => {
    const extension = `.${file.name.split(".").pop()?.toLowerCase()}`;
    if (!LMS_VALID_FILE_EXTENSIONS.split(",").includes(extension)) {
      message.error(`${file.name} is not a supported LMS file type`);
      return Upload.LIST_IGNORE;
    }
    // The backend determines the deployed environment's video cap.
    const isVideo = [".mp4", ".mov", ".webm"].includes(extension);
    const maxSize = isVideo ? 500 * 1024 * 1024 : LMS_MAX_FILE_SIZE;
    if (file.size > maxSize) {
      message.error(`${file.name} exceeds the ${isVideo ? 500 : 30} MB limit`);
      return Upload.LIST_IGNORE;
    }
    return false;
  };

  const columns = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      fixed: "left",
      render: (value, record) => (
        <Button type="link" style={{ padding: 0, fontWeight: 600 }} onClick={() => { setDetail(record); setDetailOpen(true); }}>
          {value}
        </Button>
      ),
    },
    { title: "Session", dataIndex: "sessionTitle", key: "sessionTitle" },
    {
      title: "Assignments",
      key: "assignments",
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          {(record.subjectTeacherDetails || []).map((assignment, index) => (
            <Text key={`${assignment.subjectName}-${index}`} type="secondary">{assignmentLabel(assignment)}</Text>
          ))}
        </Space>
      ),
    },
    {
      title: "Type",
      dataIndex: "content_type",
      key: "content_type",
      render: (value) => LMS_CONTENT_TYPES.find((item) => item.value === value)?.label || value,
    },
    { title: "Week", dataIndex: "week_number", key: "week_number", render: (value) => value || "—" },
    {
      title: "Files",
      key: "files",
      render: (_, record) => <Tag icon={<FileTextOutlined />}>{record.contentFiles?.length || 0}</Tag>,
    },
    { title: "Status", dataIndex: "status", key: "status", render: (status) => <Tag color={statusColor(status)}>{status}</Tag> },
    {
      title: "Access",
      key: "access",
      render: (_, record) => <LmsOwnershipTag record={record} />,
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      render: (_, record) => (
        <Space size="small">
          <Button type="text" icon={<EyeOutlined />} onClick={() => { setDetail(record); setDetailOpen(true); }} />
          {can("update_lms_content") && canEditLmsRow(record) && !["Published", "Inactive"].includes(record.status) && <Button type="text" icon={<EditOutlined />} onClick={() => openEdit(record)} />}
          {can("submit_lms_content") && canEditLmsRow(record) && record.status === "Draft" && record.contentFiles?.length > 0 && <Popconfirm title="Submit this content for HOD review?" onConfirm={() => runAction(api.content.submit, record, "Content submitted for review")}><Button type="text" icon={<SendOutlined />} /></Popconfirm>}
          {can("publish_lms_content") && record.status === "Pending HOD Review" && <Button type="text" style={{ color: "#52c41a" }} icon={<CheckCircleOutlined />} onClick={() => runAction(api.content.publish, record, "Content published")} />}
          {can("reject_lms_content") && record.status === "Pending HOD Review" && <Button type="text" danger icon={<StopOutlined />} onClick={() => setRejectRecord(record)} />}
          {can("delete_lms_content") && canEditLmsRow(record) && !["Published", "Inactive"].includes(record.status) && <Popconfirm title="Delete this content?" description="This cannot be undone." onConfirm={() => handleDelete(record)}><Button type="text" danger icon={<DeleteOutlined />} /></Popconfirm>}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <LmsPageHeader
        title="LMS content"
        description="Create learning materials, attach files, and move them through the HOD review workflow."
        extra={[
          <Button key="refresh" icon={<ReloadOutlined />} loading={loading} onClick={refresh}>Refresh</Button>,
          can("create_lms_content") && <Button key="new" type="primary" icon={<PlusOutlined />} onClick={openCreate}>New content</Button>,
        ]}
      />
      <LmsErrorAlert error={error} onClose={() => setError("")} />
      <Card>
        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          <Col xs={24} md={7}>
            <Input.Search allowClear placeholder="Search content" value={filters.search} onChange={(event) => setFilters((current) => ({ ...current, search: event.target.value }))} onSearch={(value) => setFilters((current) => ({ ...current, search: value }))} />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select allowClear showSearch optionFilterProp="label" style={{ width: "100%" }} placeholder="Session" value={filters.lms_session} onChange={(value) => setFilters((current) => ({ ...current, lms_session: value }))} options={sessions.map((session) => ({ value: session.id, label: session.title }))} />
          </Col>
          <Col xs={24} sm={12} md={5}>
            <Select allowClear style={{ width: "100%" }} placeholder="Status" value={filters.status} onChange={(value) => setFilters((current) => ({ ...current, status: value }))} options={LMS_CONTENT_STATUSES.map((value) => ({ value, label: value }))} />
          </Col>
          <Col xs={24} sm={12} md={5}>
            <Select allowClear style={{ width: "100%" }} placeholder="Content type" value={filters.content_type} onChange={(value) => setFilters((current) => ({ ...current, content_type: value }))} options={LMS_CONTENT_TYPES} />
          </Col>
        </Row>
        <Table rowKey="id" columns={columns} dataSource={content} loading={loading} scroll={{ x: 1250 }} pagination={{ ...pagination, showSizeChanger: false, showTotal: (total) => `${total} content item${total === 1 ? "" : "s"}` }} onChange={(nextPagination) => loadContent(nextPagination.current, nextPagination.pageSize)} />
      </Card>

      <Modal title={editing ? "Edit LMS content" : "New LMS content"} open={formOpen} onCancel={() => { setFormOpen(false); setEditing(null); }} onOk={() => form.submit()} confirmLoading={saving} width={680} destroyOnClose>
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Form.Item name="lmsSessionID" label="LMS session" rules={[{ required: true, message: "Select a session" }]}>
            <Select showSearch optionFilterProp="label" options={sessions.map((session) => ({ value: session.id, label: `${session.title} · ${session.status}` }))} />
          </Form.Item>
          <Form.Item name="subjectTeacherIDs" label="Subject assignments" rules={[{ required: true, message: "Select at least one subject assignment" }]}>
            <Select mode="multiple" showSearch optionFilterProp="label" options={subjectTeachers.map((assignment) => ({ value: assignment.id, label: assignmentLabel(assignment) }))} />
          </Form.Item>
          <Form.Item name="title" label="Title" rules={[{ required: true, message: "Enter a title" }]}><Input placeholder="e.g. PLE Mathematics revision pack" /></Form.Item>
          <Form.Item name="description" label="Description"><Input.TextArea rows={3} placeholder="What should learners know or do with this material?" /></Form.Item>
          <Row gutter={12}>
            <Col span={14}>
              <Form.Item name="content_type" label="Content type" rules={[{ required: true, message: "Select a content type" }]}><Select options={LMS_CONTENT_TYPES} /></Form.Item>
            </Col>
            <Col span={10}>
              <Form.Item name="week_number" label="Week number"><InputNumber min={1} max={52} style={{ width: "100%" }} /></Form.Item>
            </Col>
          </Row>
          <Form.Item label="Files" extra="Documents/audio up to 30 MB each; videos up to 500 MB in production or 50 MB in other environments. The server enforces the environment limit. Files are required before submitting for review.">
            <Upload beforeUpload={beforeUpload} multiple fileList={fileList} onChange={({ fileList: nextFiles }) => setFileList(nextFiles)} accept={LMS_VALID_FILE_EXTENSIONS}>
              <Button icon={<UploadOutlined />}>Select files</Button>
            </Upload>
            {fileList.length > 0 && <Text type="secondary">{fileList.map((file) => `${file.name} (${formatBytes(file.size)})`).join(" · ")}</Text>}
          </Form.Item>
        </Form>
      </Modal>

      <Modal title={`Reject ${rejectRecord?.title || "content"}`} open={Boolean(rejectRecord)} onCancel={() => { setRejectRecord(null); rejectForm.resetFields(); }} onOk={() => rejectForm.submit()} destroyOnClose>
        <Form form={rejectForm} layout="vertical" onFinish={handleReject}>
          <Form.Item name="rejectionReason" label="Reason"><Input.TextArea rows={4} placeholder="Explain what needs to be corrected" /></Form.Item>
        </Form>
      </Modal>

      <Drawer title={<Title level={5} style={{ margin: 0 }}>Content details</Title>} open={detailOpen} onClose={() => setDetailOpen(false)} width={620}>
        {detail && (
          <>
            <Space style={{ marginBottom: 16 }} wrap>
              {can("submit_lms_content") && canEditLmsRow(detail) && detail.status === "Draft" && detail.contentFiles?.length > 0 && (
                <Popconfirm title="Submit this content for HOD review?" onConfirm={() => runAction(api.content.submit, detail, "Content submitted for review")}>
                  <Button icon={<SendOutlined />}>Submit for review</Button>
                </Popconfirm>
              )}
              {can("update_lms_content") && canEditLmsRow(detail) && detail.status === "Draft" && (
                <Button icon={<EditOutlined />} onClick={() => { setDetailOpen(false); openEdit(detail); }}>Edit draft</Button>
              )}
              {detail.status === "Pending HOD Review" && (can("publish_lms_content") || can("reject_lms_content")) && (
                <>
                  {can("publish_lms_content") && <Button type="primary" icon={<CheckCircleOutlined />} onClick={() => runAction(api.content.publish, detail, "Content published")}>Approve and publish</Button>}
                  {can("reject_lms_content") && <Button danger icon={<StopOutlined />} onClick={() => setRejectRecord(detail)}>Reject</Button>}
                </>
              )}
              {detail.status === "Draft" && detail.rejectionReason && (
                <Text type="secondary">Edit the returned draft, then submit it again for review.</Text>
              )}
            </Space>
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="Title">{detail.title}</Descriptions.Item>
              <Descriptions.Item label="Session">{detail.sessionTitle}</Descriptions.Item>
              <Descriptions.Item label="Type">{LMS_CONTENT_TYPES.find((item) => item.value === detail.content_type)?.label || detail.content_type}</Descriptions.Item>
              <Descriptions.Item label="Week">{detail.week_number || "—"}</Descriptions.Item>
              <Descriptions.Item label="Status"><Tag color={statusColor(detail.status)}>{detail.statusDisplay || detail.status}</Tag></Descriptions.Item>
              <Descriptions.Item label="Created by">{detail.createdByName || "—"}</Descriptions.Item>
              <Descriptions.Item label="Access"><LmsOwnershipTag record={detail} /></Descriptions.Item>
              <Descriptions.Item label="Description">{detail.description || "—"}</Descriptions.Item>
              <Descriptions.Item label="Reviewed by">{detail.reviewedByName || "—"}</Descriptions.Item>
              <Descriptions.Item label="Published at">{formatDateTime(detail.publishedAt)}</Descriptions.Item>
              {detail.rejectionReason && <Descriptions.Item label="Rejection reason"><Alert type="error" message={detail.rejectionReason} /></Descriptions.Item>}
            </Descriptions>
            <Typography.Title level={5} style={{ marginTop: 24 }}>Assignments</Typography.Title>
            <List size="small" dataSource={detail.subjectTeacherDetails || []} locale={{ emptyText: "No assignments" }} renderItem={(assignment) => <List.Item>{assignmentLabel(assignment)} · {assignment.teacherName || "Teacher"}</List.Item>} />
            <Typography.Title level={5} style={{ marginTop: 24 }}>Files</Typography.Title>
            <List size="small" dataSource={detail.contentFiles || []} locale={{ emptyText: "No files uploaded" }} renderItem={(file) => <List.Item actions={[file.fileUrl && <a key="download" href={file.fileUrl} target="_blank" rel="noreferrer">Open</a>, can("delete_content_file") && <Popconfirm key="delete" title="Delete this file?" onConfirm={() => handleDeleteFile(file)}><Button type="text" danger icon={<DeleteOutlined />} /></Popconfirm>].filter(Boolean)}><Space direction="vertical" size={0}><Text>{file.title || file.original_name}</Text><Text type="secondary">{formatBytes(file.file_size)} · uploaded {formatDateTime(file.uploadedAt)}</Text></Space></List.Item>} />
            <Space style={{ marginTop: 24, marginBottom: 8 }}>
              <Typography.Title level={5} style={{ margin: 0 }}>Approval history</Typography.Title>
              <Button size="small" icon={<ReloadOutlined />} loading={approvalLoading} onClick={() => loadApprovalHistory(detail.id)}>Refresh</Button>
            </Space>
            {approvalError && <Alert type="error" showIcon message={approvalError} style={{ marginBottom: 12 }} />}
            <Timeline
              pending={approvalLoading ? "Loading approval history…" : false}
              items={approvalHistory.map((approval) => ({
                color: approval.action === "Approved" ? "green" : "red",
                children: (
                  <Space direction="vertical" size={2}>
                    <Text strong>{approval.action} · {approval.stage || "HOD"} review</Text>
                    <Text type="secondary">
                      Cycle {approval.submission_cycle} · {approval.approverName || "Reviewer"} · {formatDateTime(approval.decidedAt)}
                    </Text>
                    {approval.remarks && <Text>{approval.remarks}</Text>}
                  </Space>
                ),
              }))}
            />
            {!approvalLoading && !approvalError && approvalHistory.length === 0 && (
              <Text type="secondary">No approval decisions yet. Submissions are not recorded as approval decisions.</Text>
            )}
          </>
        )}
      </Drawer>
    </div>
  );
};

export default LmsContent;
